import { TASK_STATUS_OPTIONS, TASK_TRANSACTION_TYPE_OPTIONS } from "./constants";

/*
 * Column definitions for the Delete Item Tasks grid.
 *
 * WHICH COLUMNS. grdSchedules's 24 columns, in its order — which is qryWMItemTask's whole field list
 * minus the five it marks Visible = False (R_Transheader, ROWID, COMPANYID, MODULE, RECORDTYPE). Nothing
 * is added and nothing curated: unlike the sibling operation screens this grid is not a preview of a
 * filter, it is the list of rows about to be deleted, so showing less than AI8 does would be hiding part
 * of what the user is agreeing to.
 *
 * dataField casing matches ItemTaskDetailDTO's property names exactly, because the endpoint serialises
 * with DefaultContractResolver, i.e. PascalCase straight off the DTO.
 *
 * dataType is declared on EVERY column, deliberately. DevExtreme only infers it from the first non-null
 * value, and on this screen nulls are the norm rather than the exception — all eight lookup joins are
 * LEFT OUTER, and a Location Transfer task has no customer, no transaction header and often no lot. With
 * nothing to infer from, the column gets no filter-row editor and the user silently loses the ability to
 * filter it.
 *
 * Status and Trans Type render as lookups rather than raw letters (Architecture.md rule 3). They are
 * passed as a `lookup` object because OperationResultGrid spreads each definition onto <Column>, so there
 * is no place to nest a <Lookup> child element.
 *
 * minWidth on every column keeps `columnAutoWidth` from squeezing 24 columns into slivers: once the
 * total exceeds the container DevExtreme shows a horizontal scrollbar instead, which is how the Delphi
 * grid behaves too.
 *
 * The array is module-level and frozen because OperationResultGrid is memo-wrapped: a fresh array per
 * render would remount GridLayoutManager and re-fire GetGridLayouts on every keystroke.
 */

const TEXT = "string";
const NUMBER = "number";
// The Delphi fields are TAbDateTimeField with tfLocal storage and display, and the grid shows the time
// (e.g. "8/19/2026 2:29:39 PM") — so datetime, not date.
const DATETIME = "datetime";

// Widths tuned to the caption plus a plausible value, so nothing truncates at the default zoom.
const W_SHORT = 90;
const W_CODE = 130;
const W_WIDE = 170;

export const GRID_KEY_EXPR = "RowId";

const column = (dataField, caption, dataType, minWidth, extra) => ({
    dataField,
    caption,
    dataType,
    minWidth,
    ...extra,
});

const text = (dataField, caption, width = W_CODE) => column(dataField, caption, TEXT, width);
const number = (dataField, caption, width = W_SHORT) => column(dataField, caption, NUMBER, width);
const datetime = (dataField, caption) => column(dataField, caption, DATETIME, W_WIDE);

const lookup = (dataSource) => ({
    lookup: { dataSource, valueExpr: "Value", displayExpr: "Text" },
});

/*
 * The key. Hidden and kept out of the column chooser (Architecture.md rule 8) — users never see raw ids,
 * and AI8 did not show it either: the dataset carries ROWID but marks it Visible = False.
 */
const rowIdColumn = { dataField: "RowId", dataType: TEXT, visible: false, showInColumnChooser: false };

/*
 * Captions verbatim from the DFM DisplayLabels, including "SubLot" with its capital L and the
 * abbreviated weight captions.
 *
 * Note the collision worth knowing about while reading the page: "Start Date" here is when work on the
 * TASK began, and has nothing to do with the Start Date question that drives the cut-off. Both captions
 * are the Delphi's and neither is renamed, because renaming either would make the screen disagree with
 * AI8 about the other.
 */
export const GRID_COLUMNS = Object.freeze([
    rowIdColumn,
    text("RecordNumber", "Record #", W_SHORT),
    column("Status", "Status", TEXT, W_CODE, lookup(TASK_STATUS_OPTIONS)),
    column("TransactionType", "Trans Type", TEXT, W_WIDE, lookup(TASK_TRANSACTION_TYPE_OPTIONS)),
    text("TransactionNumber", "Trans Number"),
    number("Priority", "Priority"),
    datetime("AssignmentDate", "Assignment Date"),
    datetime("CompletedDate", "Completed Date"),
    number("GrossWeightQuantity", "Gross Wt Qty", W_CODE),
    text("GrossWeightUom", "Gross Wt UOM", W_CODE),
    number("NetWeightQuantity", "Net Wt Qty", W_CODE),
    text("NetWeightUom", "Net Wt UOM", W_CODE),
    number("ElapsedHours", "Elapsed Hours", W_CODE),
    number("ElapsedMinutes", "Elapsed Minutes", W_CODE),
    datetime("StartDate", "Start Date"),
    datetime("EndDate", "End Date"),
    text("CustomerId", "Customer"),
    text("ItemCode", "Item"),
    text("LotCode", "Lot"),
    text("SublotCode", "SubLot"),
    text("UnitCode", "Unit"),
    text("SerialNumber", "Serial"),
    text("Warehouse", "Warehouse"),
    text("LocationCode", "Location"),
    text("Worker", "Worker"),
]);
